import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  Max as MaxSchema,
  type Max,
  buildCursorPath,
  formatWeight,
  repsIntensityBuckets,
} from "@heraion/shared";

import { ChartCappedNote } from "../charts/ChartCappedNote";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { PanelTitleRow } from "./InfoPill";
import {
  cellAlpha,
  computeIntensityPickable,
  mostTrainedSlug,
  heatCellColors,
  maxCell,
  sumMatrix,
} from "./tierTwoMath";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import type { ProgressApiFetch, ProgressDeps } from "./types";
import { useExerciseNameResolver } from "./useExerciseNameResolver";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { useWindowSets } from "./useWindowSets";
import { weightUnitLabels } from "../weightUnitLabels";

const REP_LABELS = ["1-3", "4-6", "7-10", "11+"];
const INT_LABELS = ["<50%", "50-70%", "70-85%", "85%+"];

// The four %1RM columns map 1:1 onto the shared intensityScale bands, so each
// column carries the same training meaning as the program-preview grid:
// endurance/warm-up (work) → hypertrophy (moderate) → heavy → max (85%+).
const COLUMN_BANDS = ["work", "moderate", "heavy", "max"] as const;

function useMaxes(apiFetch: ProgressApiFetch) {
  const [maxes, setMaxes] = useState<ReadonlyArray<Max> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch<unknown>("/api/me/maxes");
        if (cancelled) return;
        const arr = Array.isArray(resp) ? resp : [];
        const parsed: Max[] = [];
        for (const raw of arr) {
          const p = MaxSchema.safeParse(raw);
          if (p.success) parsed.push(p.data);
        }
        setMaxes(parsed);
      } catch {
        if (!cancelled) setMaxes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return maxes;
}

// 4×4 reps × %1RM grid per exercise. Per-exercise lazy fetch lights up when
// the user picks an exercise.
export function IntensityHeatmapPanel(deps: ProgressDeps) {
  const { theme, apiFetch, units } = deps;
  const { t, i18n } = useTranslation();
  // The translated unit word for every weight below (CN-26 ondata 2).
  const unitLabels = weightUnitLabels(t);
  const { results, catalog } = useSessionResultsFromDeps(deps);
  const maxes = useMaxes(apiFetch);
  const getName = useExerciseNameResolver(catalog);
  const [pickerOpen, setPickerOpen] = useState(false);
  // PG-64: `undefined` = the user has not chosen, so the most-trained exercise
  // is shown; any manual choice (including "None") is never overwritten.
  const [manualSlug, setSlug] = useState<string | null | undefined>(undefined);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);

  // Pickable = anything the user has both logged AND has a MAX recorded for
  // (the matrix denominator needs the MAX).
  const pickable = useMemo(() => {
    if (!results || !maxes) return null;
    return computeIntensityPickable(results, maxes).map((p) => ({
      ...p,
      name: getName(p.slug, p.snapshot),
    }));
  }, [results, maxes, getName]);

  const slug: string | null =
    manualSlug !== undefined
      ? manualSlug
      : pickable
        ? mostTrainedSlug(pickable)
        : null;
  // An error reads as no sets, as it always did here.
  const { sets: loaded, error: setsError, capped } = useWindowSets(
    apiFetch,
    deps.range,
    ({ sinceIso, untilIso, cursor }) =>
      slug
        ? buildCursorPath(
            "/api/sets",
            `exercise=${encodeURIComponent(slug)}${
              sinceIso ? `&since=${encodeURIComponent(sinceIso)}` : ""
            }${untilIso ? `&until=${encodeURIComponent(untilIso)}` : ""}&limit=500`,
            cursor,
          )
        : null,
    (resp) => (resp as { items?: unknown[] }).items ?? [],
    slug ?? "",
  );
  const sets = useMemo(
    () => (setsError ? [] : slug ? loaded : null),
    [setsError, slug, loaded],
  );

  const userMaxKg = useMemo(() => {
    if (!slug || !maxes) return 0;
    return maxes.find((m) => m.exerciseId === slug)?.valueKg ?? 0;
  }, [slug, maxes]);

  const matrix = useMemo(() => {
    if (!sets || userMaxKg <= 0) return null;
    return repsIntensityBuckets(sets, userMaxKg);
  }, [sets, userMaxKg]);

  const title = t("mobile.progress.intensityHeatmap.title", {
    defaultValue: "Intensity heatmap",
  });
  const help = t("mobile.progress.intensityHeatmap.help", {
    defaultValue:
      "Per-exercise grid of completed sets sorted by rep range and intensity (% of 1RM). Strength range (85%+) at the bottom right, hypertrophy in the middle, endurance top left. Pick an exercise above to see where its sets cluster.",
  });

  const pickerLabel =
    pickable === null
      ? t("maxes.loading", { defaultValue: "Loading maxes…" })
      : slug
        ? (pickable.find((p) => p.slug === slug)?.name ?? slug)
        : t("mobile.progress.intensityHeatmap.exerciseLabel", {
            defaultValue: "Exercise",
          });

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={title}
        body={help}
        period={t("mobile.progress.period.all", { defaultValue: "all" })}
      />
      <Pressable
        accessibilityRole="button"
        onPress={() => setPickerOpen((v) => !v)}
        style={({ pressed }) => [
          styles.heatPicker,
          pressed && styles.heatPickerPressed,
        ]}
      >
        <Text style={styles.heatPickerLabel} numberOfLines={1}>
          {pickerLabel}
        </Text>
        <Text style={styles.heatPickerChevron}>{pickerOpen ? "▴" : "▾"}</Text>
      </Pressable>
      {pickerOpen && pickable !== null ? (
        <View style={styles.heatPickerList}>
          {/* Clear back to the empty state. Without this the picker only
              ever sets a non-null slug, so once an exercise is chosen there
              is no way back to "Choose an exercise" (the empty heatmap). */}
          {slug !== null ? (
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                setSlug(null);
                setPickerOpen(false);
              }}
              style={({ pressed }) => [
                styles.heatPickerItem,
                pressed && styles.heatPickerItemPressed,
              ]}
            >
              <Text
                style={[styles.heatPickerItemText, styles.heatPickerClearText]}
                numberOfLines={1}
              >
                {t("mobile.progress.intensityHeatmap.clear", {
                  defaultValue: "None",
                })}
              </Text>
            </Pressable>
          ) : null}
          {pickable.length === 0 ? (
            <Text style={styles.empty}>
              {t("mobile.progress.intensityHeatmap.noEligible", {
                defaultValue:
                  "Record a 1RM on at least one exercise to compute intensity.",
              })}
            </Text>
          ) : (
            pickable.slice(0, 12).map((p) => (
              <Pressable
                key={p.slug}
                onPress={() => {
                  setSlug(p.slug);
                  setPickerOpen(false);
                }}
                style={({ pressed }) => [
                  styles.heatPickerItem,
                  pressed && styles.heatPickerItemPressed,
                ]}
              >
                <Text style={styles.heatPickerItemText} numberOfLines={1}>
                  {p.name}
                </Text>
              </Pressable>
            ))
          )}
        </View>
      ) : null}
      {slug !== null && matrix !== null ? (
        <>
          <View style={styles.heatHeaderRow}>
            <View style={styles.heatRowHeader} />
            {INT_LABELS.map((lbl) => (
              <Text key={lbl} style={styles.heatAxisLabel}>
                {lbl}
              </Text>
            ))}
          </View>
          {matrix.map((row, ri) => {
            const peak = maxCell(matrix) || 1;
            return (
              <View key={ri} style={styles.heatRow}>
                <Text style={[styles.heatAxisLabel, styles.heatRowHeader]}>
                  {REP_LABELS[ri]}
                </Text>
                {row.map((count, ci) => {
                  if (count === 0) {
                    return (
                      <View
                        key={ci}
                        style={[
                          styles.heatCell,
                          { backgroundColor: theme.palette.surface },
                        ]}
                      />
                    );
                  }
                  // Colour by intensity zone (column), opacity by set count;
                  // the count number gets the most-legible text colour for the
                  // composited cell so it never vanishes (inbox b5b69fc9 /
                  // bcb8f1e3, both themes).
                  const band = theme.intensity[COLUMN_BANDS[ci] ?? "max"];
                  const { background, text } = heatCellColors(
                    band.fill,
                    cellAlpha(count, peak),
                    theme.palette.surface,
                    [band.on, theme.palette.ink, theme.palette.inkOnBrand],
                  );
                  return (
                    <View
                      key={ci}
                      style={[styles.heatCell, { backgroundColor: background }]}
                    >
                      <Text style={[styles.heatCellText, { color: text }]}>
                        {count}
                      </Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
          <ScaleLegend
            variant="bands"
            theme={theme}
            style={styles.heatLegend}
          />
          <Text style={styles.heatFooter}>
            {t("mobile.progress.intensityHeatmap.totalSets", {
              defaultValue: "{{n}} sets · 1RM {{max}}",
              n: sumMatrix(matrix),
              max: formatWeight(userMaxKg, units, i18n.language, unitLabels),
            })}
          </Text>
          {capped ? <ChartCappedNote theme={theme} /> : null}
        </>
      ) : slug !== null && sets !== null && userMaxKg <= 0 ? (
        <Text style={styles.empty}>
          {t("mobile.progress.intensityHeatmap.noMax", {
            defaultValue: "No 1RM recorded for this exercise yet.",
          })}
        </Text>
      ) : slug !== null && sets === null ? (
        <ActivityIndicator color={theme.palette.brand500} />
      ) : null}
    </View>
  );
}
